// @<COPYRIGHT>@
// ==================================================
// Copyright (c) 2020 Siemens
// Siemens Product Lifecycle Management Software Inc.
// All Rights Reserved.
// ==================================================
// @<COPYRIGHT>@

/**
 * This module provides workspace interface for application team.
 *
 * @module js/swf3dWorkspace
 */

import appCtxSvc from 'js/appCtxService';
import API from 'js/swf3dWorkspaceAPI';
import { http, pubSub } from '@swf3d/utils';
import config from 'js/swf3dWorkspaceConfig';
import utils from 'js/swf3dWorkspaceUtils';
import defaultValue from 'js/swf3dWorkspaceDefaultValue';
import { setGenTableCB, setCheckRowDataShowCB, setSelectedObjectCB } from 'js/swf3dWorkspaceCallbacks';
import input from 'js/swf3dWorkspaceInput';

// Interface for application team to setup workspace.
let swf3dWorkspace = {};

/**
 * Init function for setup workspace toolkit.
 * This function will run automatically when imported by other js file.
 *
 * @memberof js/swf3dWorkspace
 * @function init
 * @param options.URL
 * @param {object} options - init options.
 * @example
 * swf3dWorkspace.init( {URL: {collab: 'https://collab.us-east-1.sws.siemens.com', workspace:'/workspaceProxyUrl'}});
 */
swf3dWorkspace.init = function ({ URL }) {
    // Setup URL.
    if (utils.isPresent(URL)) {
        if (utils.isPresent(URL.workspace)) {
            config.url = URL.workspace;
        }
        config.collabHost = URL.collab;
        config.dssHost = URL.dss;
        let previewHost = URL.preview;
        if (utils.isPresent(previewHost)) {
            config.previewHost = previewHost + '/sendAPI';
        } else {
            config.previewHost = defaultValue.previewUrl;
        }
    }

    // Setup app info.
    let appInfo = {
        appName: undefined,
        appHost: window.location.hostname
    };
    if (utils.isPresent(appCtxSvc.getCtx('workspace.workspaceName'))) {
        appInfo.appName = appCtxSvc.getCtx('workspace.workspaceName');
    } else if (utils.isPresent(appCtxSvc.getCtx('galaxy.headerTitle'))) {
        appInfo.appName = appCtxSvc.getCtx('galaxy.headerTitle');
    }
    config.appInfo = appInfo;

    // Setup filter list.
    config.validTypeList = defaultValue.validTypeList;
    config.tableFilterList = defaultValue.tableFilterList;

    // Setup default value of ctx.
    appCtxSvc.updatePartialCtx('galaxy.workspace.upload.filterList', defaultValue.uploadFilterList);
    appCtxSvc.updatePartialCtx('galaxy.workspace.table.currentFilterList', defaultValue.tableFilterList);
    appCtxSvc.updatePartialCtx('galaxy.workspace.showFilterPanel', 'true');
    appCtxSvc.updatePartialCtx('galaxy.workspace.isEnableUpload', 'true');
    appCtxSvc.updatePartialCtx('galaxy.workspace.tcReady', 'false');

    this.isInitialized = true;
};

/**
 * Config Zeus collab
 *
 * @memberof js/swf3dWorkspace
 * @function collab
 * @param {object} collabConifg - collab conifg.
 * @example
 * swf3dWorkspace.collab( {projectId: 'collabProjectId', collabSpaceId:'collabSpaceId'});
 */
swf3dWorkspace.collab = function (collabConifg = {}) {
    config.storage = 'collab';
    if (utils.isPresent(collabConifg.projectId)) {
        config.resourceType = 'objects';
        config.projectId = collabConifg.projectId;
    }
    if (utils.isPresent(collabConifg.collabSpaceId)) {
        config.collabSpaceId = collabConifg.collabSpaceId;
    } else {
        config.ready = new Promise(resolve => {
            API.getCollaborationspaceId()
                .then(collabSpaceId => {
                    config.collabSpaceId = collabSpaceId;
                    // @ts-ignore
                    resolve(true);
                })
                .catch(err => {
                    // for NSAF-1590, show service error
                    pubSub.publish('galaxyWorkspaceError', [err]);
                });
        });
    }
    appCtxSvc.updatePartialCtx('galaxy.workspace.storage', 'collab');
    appCtxSvc.updatePartialCtx('galaxy.workspace.baseUrn', '');
};

/**
 * Config TC
 *
 * @memberof js/swf3dWorkspace
 * @function TC
 * @param tcConifg.tenantId
 * @param {object} tcConifg - collab conifg.
 * @example
 * swf3dWorkspace.TC( {tenantId: 'tenantId'});
 */
swf3dWorkspace.TC = function ({ tenantId = undefined }) {
    sessionStorage.setItem('workspaceMode', 'tc');
    config.storage = 'tc';
    config.resourceType = 'objects';

    if (utils.isPresent(tenantId)) {
        config.set('tenantId', tenantId);
    }

    appCtxSvc.updatePartialCtx('galaxy.workspace.storage', 'tc');
    appCtxSvc.updatePartialCtx('galaxy.workspace.baseUrn', '');
};

/**
 * Config local
 *
 * @memberof js/swf3dWorkspace
 * @function local
 * @param {string} localPath - Local storage path.
 * @example
 * swf3dWorkspace.local("D:\workspace\prt");
 */
swf3dWorkspace.local = function (localPath) {
    config.storage = 'local';
    config.baseUrn = localPath;
    config.resourceType = 'objects';
    appCtxSvc.updatePartialCtx('galaxy.workspace.storage', 'local');
    appCtxSvc.updatePartialCtx('galaxy.workspace.baseUrn', localPath);
    appCtxSvc.updatePartialCtx('galaxy.workspace.tableCurrentFolderUrn', localPath);
};

/**
 * Config upload filter condition
 *
 * @memberof js/swf3dWorkspace
 * @function setUploadFilterList
 * @param {string} filterList - File types
 * @example
 * swf3dWorkspace.filterList("prt,jt,sim");
 */
swf3dWorkspace.setUploadFilterList = function (filterList) {
    appCtxSvc.updatePartialCtx('galaxy.workspace.upload.filterList', filterList);
};

/**
 * Interface for customize function of generate table.
 *
 * @memberof js/swf3dWorkspace
 * @function setGenTableCB
 * @param {Function} fun - customized funtion
 * @example
 * swf3dWorkspace.setGenTableCB((column, item)=> { return {
        dbValues: item.urn,
        uiValues: item.name,
        type: 'STRING'
    }});
 */
swf3dWorkspace.setGenTableCB = function (fun) {
    if (utils.isPresent(fun)) {
        setGenTableCB(fun);
    }
};

/**
 * Interface for customize function of generate table row.
 *
 * @memberof js/swf3dWorkspace
 * @function setCheckRowDataShowCB
 * @param {Function} fun - customized funtion
 * @example
 * swf3dWorkspace.setCheckRowDataShowCB((eData)=> {return true});
 */
swf3dWorkspace.setCheckRowDataShowCB = function (fun) {
    if (utils.isPresent(fun)) {
        setCheckRowDataShowCB(fun);
    }
};

/**
 * Interface for customize function of when object be selected.
 *
 * @memberof js/swf3dWorkspace
 * @function setSelectedObjectCB
 * @param {Function} fun - customized funtion
 * @example
 * swf3dWorkspace.setSelectedObjectCB((data, viewModelData)=> {return data});
 */
swf3dWorkspace.setSelectedObjectCB = function (fun) {
    if (utils.isPresent(fun)) {
        setSelectedObjectCB(fun);
    }
};

/**
 * Interface for setup if show filter panel.
 *
 * @memberof js/swf3dWorkspace
 * @function setShowFilterPanel
 * @param {boolean} isShow - Show filter panel or not
 * @example
 * swf3dWorkspace.setShowFilterPanel(true);
 */
swf3dWorkspace.setShowFilterPanel = function (isShow) {
    if (true === isShow) {
        appCtxSvc.updatePartialCtx('galaxy.workspace.showFilterPanel', 'true');
    } else {
        appCtxSvc.updatePartialCtx('galaxy.workspace.showFilterPanel', 'false');
    }
};

/**
 * Interface for setup if show preview.
 *
 * @memberof js/swf3dWorkspace
 * @function setShowPreview
 * @param {boolean} isShow - Show part preview or not
 * @example
 * swf3dWorkspace.setShowPreview(true);
 */
swf3dWorkspace.setShowPreview = function (isShow) {
    config.showPreview = isShow;
};

/**
 * Interface for setup table filter list.
 *
 * @memberof js/swf3dWorkspace
 * @function setTableFilterList
 * @param {string} filterList - Filter list
 * @example
 * swf3dWorkspace.setTableFilterList("prt,jt,excel");
 */
swf3dWorkspace.setTableFilterList = function (filterList) {
    config.tableFilterList = filterList;
    appCtxSvc.updatePartialCtx('galaxy.workspace.table.currentFilterList', filterList);
};

/**
 * Interface for get objects by urn.
 *
 * @memberof js/swf3dWorkspace
 * @function getObjects
 * @param {object} getOptions - get object options
 * @example
 * swf3dWorkspace.getObjects({});
 * @returns {Promise} - collab part objects
 */
swf3dWorkspace.getObjects = function (getOptions) {
    return config.ready.then(res => {
        let url = config.url + '/' + config.storage + '/objects';
        let inputData = { ...input.getCollabInputData(), ...getOptions };

        return http.get(url, { data: JSON.stringify(inputData) });
    });
};

export default swf3dWorkspace;
